import React, { useState, useCallback } from 'react';
import { Card, Typography, Row, Col, Button, Descriptions, Tag, message, Empty, Tabs, Space, Spin, Statistic } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useAuth } from '@/contexts/AuthContext';
import { getWorkspaceById, deleteWorkspace } from '@/api/workspaces';
import DeleteConfirmationModal from '@/components/common/DeleteConfirmationModal';
import WorkspaceEditModal from '@/components/features/workspace/WorkspaceEditModal';
// Tour functionality removed

const { Title } = Typography;

const WorkspacePage: React.FC = () => {
  const { t } = useTranslation(['workspace', 'common']);
  const { firestoreUser } = useAuth();
  const queryClient = useQueryClient();
  // Tour functionality removed
  
  // State for modals
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [deleteError, setDeleteError] = useState<(Error & { dependencies?: { type: string; items: any[] }[]; blockName?: string; unitNumber?: string; amenityName?: string }) | undefined>(undefined);

  // Get workspace ID from user context
  const workspaceId = firestoreUser?.workspace?.id;

  // Query to fetch workspace data
  const { data: workspace, isLoading, error } = useQuery({
    queryKey: ['workspace', workspaceId],
    queryFn: async () => {
      if (!workspaceId) {
        throw new Error('No workspace ID available');
      }
      return getWorkspaceById(workspaceId);
    },
    enabled: !!workspaceId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return deleteWorkspace(id);
    },
    onSuccess: () => {
      message.success(t('workspace:success.delete', 'Workspace deleted successfully'));
      queryClient.invalidateQueries({ queryKey: ['workspace'] });
      setOpenDeleteDialog(false);
      setDeleteError(undefined);
    },
    onError: (error: Error) => {
      setDeleteError(error);
    }
  });

  // Handlers
  const handleEdit = useCallback(() => {
    setOpenEditModal(true);
  }, []);

  const handleEditSuccess = useCallback(() => {
    setOpenEditModal(false);
  }, []);

  const handleEditCancel = useCallback(() => {
    setOpenEditModal(false);
  }, []);

  const handleDelete = useCallback(() => {
    setDeleteError(undefined);
    setOpenDeleteDialog(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (workspaceId) {
      deleteMutation.mutate(workspaceId);
    }
  }, [workspaceId, deleteMutation]);

  const handleCancelDelete = () => {
    setOpenDeleteDialog(false);
    setDeleteError(undefined);
  };

  const handleErrorDismiss = () => {
    setDeleteError(undefined);
  };

  // Loading state
  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Spin size="large" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <Card>
        <Empty 
          description={t('common:errors.loadingError', 'Error loading workspace data')} 
        />
      </Card>
    );
  }

  // No workspace found
  if (!workspace) {
    return (
      <Card>
        <Empty 
          description={t('common:errors.notFound', { entity: t('workspace:title', 'Workspace') })} 
        />
      </Card>
    );
  }

  const items = [
    {
      key: 'overview',
      label: t('workspace:tabs.overview'),
      children: (
        <Card>
          <Descriptions bordered column={1}>
            <Descriptions.Item label={t('workspace:form.status')}>
              <Tag color={workspace.status === 'active' ? 'success' : 'default'}>
                {t(`workspace:status.${workspace.status}`)}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t('workspace:form.customer')}>
              {workspace.customer_id || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('workspace:table.name')}>
              {workspace.name}
            </Descriptions.Item>
            <Descriptions.Item label={t('workspace:table.slug')}>
              {workspace.slug}
            </Descriptions.Item>
            <Descriptions.Item label={t('workspace:table.language')}>
              {workspace.language || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('workspace:table.currency')}>
              {workspace.currency || '-'}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      ),
    },
    {
      key: 'president',
      label: t('workspace:tabs.president'),
      children: (
        <Card>
          <Descriptions bordered column={1}>
            <Descriptions.Item label={t('workspace:table.presidentName')}>
              {workspace.presidentName || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('workspace:table.presidentEmail')}>
              {workspace.presidentEmail || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('workspace:table.presidentPhone')}>
              {workspace.presidentPhone || '-'}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      ),
    },
    {
      key: 'stats',
      label: t('workspace:tabs.stats'),
      children: workspace.stats ? (
        <Card>
          <Descriptions bordered column={1}>
            <Statistic
              title={t('workspace:totalUnits')}
              value={workspace.stats.totalAssets}
            />
            <Statistic
              title={t('workspace:totalUsers')}
              value={workspace.stats.totalUsers}
            />
          </Descriptions>
        </Card>
      ) : <Empty description={t('common:messages.noData')} />,
    },
  ];

  return (
    <div style={{ width: '100%' }}>
      <Row justify="space-between" align="middle" gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>{workspace.name}</Title>
        </Col>
        <Col>
          <Space wrap>
            <Button 
              icon={<EditOutlined />} 
              className="ant-btn-primary"
              onClick={handleEdit}
            >
              {t('common:actions.edit')}
            </Button>
            <Button 
              danger 
              icon={<DeleteOutlined />} 
              onClick={handleDelete}
              loading={deleteMutation.isPending}
            >
              {t('common:actions.delete')}
            </Button>
          </Space>
        </Col>
      </Row>

      <Tabs items={items} />

      {/* Edit Modal */}
      <WorkspaceEditModal
        open={openEditModal}
        workspace={workspace}
        onCancel={handleEditCancel}
        onSuccess={handleEditSuccess}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        visible={openDeleteDialog}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title={t('common:confirmDeleteTitle', 'Delete Workspace')}
        error={deleteError}
        onErrorDismiss={handleErrorDismiss}
      />
    </div>
  );
};

export default WorkspacePage; 